<template>
  <div class="page-wrapper">
    <div class="login-card">

      <!-- Brand header -->
      <div class="brand-header">
        <div class="brand-icon">
          <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="20" fill="url(#grad)"/>
            <path d="M13 20.5l5 5 9-10" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            <defs>
              <linearGradient id="grad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                <stop stop-color="#6c47ff"/>
                <stop offset="1" stop-color="#00c6ff"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
        <h1 class="brand-title">Microboss</h1>
        <p class="brand-subtitle" v-if="step === 'credentials'">Sign in to your account</p>
        <p class="brand-subtitle" v-else>Enter your verification code</p>
      </div>

      <!-- Step indicator -->
      <div class="steps">
        <div class="step" :class="{ active: step === 'credentials', done: step === 'otp' }">
          <span class="step-dot">{{ step === 'otp' ? '✓' : '1' }}</span>
          <span class="step-label">Credentials</span>
        </div>
        <div class="step-line"></div>
        <div class="step" :class="{ active: step === 'otp' }">
          <span class="step-dot">2</span>
          <span class="step-label">Verify OTP</span>
        </div>
      </div>

      <!-- Credentials form -->
      <form v-if="step === 'credentials'" @submit.prevent="requestOtp" class="login-form">
        <div class="field-group">
          <label>Username / Email / Employee ID</label>
          <div class="input-wrap">
            <svg class="field-icon" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="7" r="3.5" stroke="#a78bfa" stroke-width="1.5"/><path d="M3 17c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="#a78bfa" stroke-width="1.5" stroke-linecap="round"/></svg>
            <input v-model="username" type="text" placeholder="Enter username, email, or employee ID" autocomplete="username" required />
          </div>
        </div>
        <div class="field-group">
          <label>Password</label>
          <div class="input-wrap">
            <svg class="field-icon" viewBox="0 0 20 20" fill="none"><rect x="4" y="9" width="12" height="9" rx="2" stroke="#a78bfa" stroke-width="1.5"/><path d="M7 9V6a3 3 0 016 0v3" stroke="#a78bfa" stroke-width="1.5" stroke-linecap="round"/></svg>
            <input v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="Enter password" autocomplete="current-password" required />
            <button type="button" class="toggle-pw" @click="showPassword = !showPassword" tabindex="-1">
              <svg v-if="!showPassword" viewBox="0 0 20 20" fill="none"><path d="M1 10s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z" stroke="#a78bfa" stroke-width="1.5"/><circle cx="10" cy="10" r="2.5" stroke="#a78bfa" stroke-width="1.5"/></svg>
              <svg v-else viewBox="0 0 20 20" fill="none"><path d="M3 3l14 14M8.5 8.7A2.5 2.5 0 0013 12M1 10s3.5-6 9-6c1.5 0 2.9.3 4.1.9M19 10s-1.3 2.2-3.5 3.8" stroke="#a78bfa" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>
          </div>
        </div>
        <div v-if="error" class="banner error-banner">{{ error }}</div>
        <div v-if="message" class="banner info-banner">{{ message }}</div>
        <button type="submit" class="btn-primary" :disabled="loading">
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Sending OTP…' : 'Send OTP' }}
        </button>
      </form>

      <!-- OTP form -->
      <form v-else @submit.prevent="verifyOtp" class="login-form">
        <div class="otp-info">
          <svg viewBox="0 0 20 20" fill="none"><path d="M10 2a8 8 0 100 16A8 8 0 0010 2z" stroke="#6c47ff" stroke-width="1.5"/><path d="M10 9v5M10 7v.01" stroke="#6c47ff" stroke-width="1.5" stroke-linecap="round"/></svg>
          <span>OTP sent to your registered email</span>
        </div>

        <div class="field-group">
          <label>6-Digit OTP</label>
          <div class="input-wrap">
            <svg class="field-icon" viewBox="0 0 20 20" fill="none"><path d="M4 10h12M10 4v12" stroke="#a78bfa" stroke-width="1.5" stroke-linecap="round"/></svg>
            <input v-model="otp" type="text" inputmode="numeric" pattern="[0-9]{6}" placeholder="000000" maxlength="6" autocomplete="one-time-code" required class="otp-input" />
          </div>
        </div>

        <!-- Countdown timer -->
        <div class="timer-row" :class="{ 'timer-warning': otpSeconds <= 30, 'timer-expired': otpSeconds === 0 }">
          <svg viewBox="0 0 20 20" fill="none"><circle cx="10" cy="11" r="7" stroke="currentColor" stroke-width="1.5"/><path d="M10 7v4l2.5 2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M8 2h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          <span v-if="otpSeconds > 0">OTP expires in <strong>{{ timerDisplay }}</strong></span>
          <span v-else>OTP has <strong>expired</strong> — please go back and login again</span>
        </div>

        <div v-if="error" class="banner error-banner">{{ error }}</div>
        <div v-if="message" class="banner info-banner">{{ message }}</div>

        <button type="submit" class="btn-primary" :disabled="loading || otpSeconds === 0">
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Verifying…' : 'Verify OTP' }}
        </button>
        <button type="button" class="btn-secondary" :disabled="loading" @click="resetToLogin">← Back to login</button>
      </form>

    </div>
  </div>
</template>

<script>
import api from '../api';

export default {
  name: 'Login',
  data() {
    return {
      username: '',
      password: '',
      showPassword: false,
      canonicalUsername: '',
      otp: '',
      error: '',
      message: '',
      step: 'credentials',
      loading: false,
      otpSeconds: 180,
      otpInterval: null,
    };
  },
  computed: {
    timerDisplay() {
      const m = Math.floor(this.otpSeconds / 60);
      const s = this.otpSeconds % 60;
      return `${m}:${String(s).padStart(2, '0')}`;
    },
  },
  beforeDestroy() {
    this._clearTimer();
  },
  methods: {
    _clearTimer() {
      if (this.otpInterval) {
        clearInterval(this.otpInterval);
        this.otpInterval = null;
      }
    },
    _startTimer() {
      this._clearTimer();
      this.otpSeconds = 180;
      this.otpInterval = setInterval(() => {
        if (this.otpSeconds > 0) {
          this.otpSeconds -= 1;
        } else {
          this._clearTimer();
        }
      }, 1000);
    },

    async requestOtp() {
      this.loading = true;
      this.error = '';
      this.message = '';
      try {
        const res = await api.post('/login', {
          username: this.username,
          password: this.password,
        });
        if (res.data && res.data.success && res.data.requiresOtp) {
          this.canonicalUsername = res.data.username || this.username;
          this.step = 'otp';
          this.message = res.data.message || 'OTP sent to your email.';
          this._startTimer();
        } else {
          this.error = 'Invalid credentials';
        }
      } catch (err) {
        this.error =
          err && err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : 'Unable to send OTP';
      } finally {
        this.loading = false;
      }
    },

    async verifyOtp() {
      this.loading = true;
      this.error = '';
      this.message = '';
      try {
        const res = await api.post('/login/verify-otp', {
          username: this.canonicalUsername || this.username,
          otp: this.otp,
        });

        if (res.data && res.data.success) {
          this._clearTimer();
          const role = String(res.data.role || '').toLowerCase();
          const isSuperUser = !!res.data.isSuperUser;
          if (!isSuperUser && role === 'dcm') {
            this.$router.replace('/dcm-dashboard').catch(() => {});
          } else if (!isSuperUser && role === 'creative') {
            this.$router.replace('/creative-dashboard').catch(() => {});
          } else {
            this.$router.replace('/').catch(() => {});
          }
          return;
        }

        this.error = 'Invalid OTP';
      } catch (err) {
        this.error =
          err && err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : 'OTP verification failed';
      } finally {
        this.loading = false;
      }
    },

    resetToLogin() {
      this._clearTimer();
      this.step = 'credentials';
      this.otp = '';
      this.error = '';
      this.message = '';
    },
  },
};
</script>

<style scoped>
/* ── Page wrapper ───────────────────────────────────────── */
.page-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  padding: 24px;
}

/* ── Card ───────────────────────────────────────────────── */
.login-card {
  width: 100%;
  max-width: 420px;
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 40px 36px 36px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  color: #fff;
}

/* ── Brand header ───────────────────────────────────────── */
.brand-header {
  text-align: center;
  margin-bottom: 28px;
}

.brand-icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 12px;
}

.brand-icon svg {
  width: 100%;
  height: 100%;
}

.brand-title {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: linear-gradient(90deg, #a78bfa, #38bdf8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 4px;
}

.brand-subtitle {
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0;
}

/* ── Step indicator ─────────────────────────────────────── */
.steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 28px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.step-dot {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.4);
  transition: all 0.3s;
}

.step.active .step-dot {
  background: linear-gradient(135deg, #6c47ff, #00c6ff);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 0 14px rgba(108, 71, 255, 0.6);
}

.step.done .step-dot {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border-color: transparent;
  color: #fff;
}

.step-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
  transition: color 0.3s;
}

.step.active .step-label,
.step.done .step-label {
  color: rgba(255, 255, 255, 0.8);
}

.step-line {
  flex: 1;
  height: 2px;
  background: rgba(255, 255, 255, 0.15);
  margin: 0 10px;
  margin-bottom: 18px;
  min-width: 40px;
}

/* ── Form ───────────────────────────────────────────────── */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.field-group label {
  font-size: 12.5px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.65);
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.field-icon {
  position: absolute;
  left: 13px;
  width: 17px;
  height: 17px;
  pointer-events: none;
  flex-shrink: 0;
}

.input-wrap input {
  width: 100%;
  background: rgba(255, 255, 255, 0.08);
  border: 1.5px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  padding: 11px 44px 11px 40px;
  font-size: 15px;
  color: #fff;
  outline: none;
  transition: border-color 0.25s, background 0.25s, box-shadow 0.25s;
  box-sizing: border-box;
}

.input-wrap input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.input-wrap input:focus {
  border-color: #a78bfa;
  background: rgba(167, 139, 250, 0.1);
  box-shadow: 0 0 0 3px rgba(167, 139, 250, 0.18);
}

/* OTP large centered digits */
.otp-input {
  letter-spacing: 6px;
  font-size: 22px !important;
  font-weight: 700;
  text-align: center;
  padding-left: 16px !important;
  padding-right: 16px !important;
}

.toggle-pw {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.toggle-pw svg {
  width: 17px;
  height: 17px;
}

/* ── OTP info banner ────────────────────────────────────── */
.otp-info {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(108, 71, 255, 0.15);
  border: 1px solid rgba(108, 71, 255, 0.35);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  color: #c4b5fd;
}

.otp-info svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* ── Countdown timer ────────────────────────────────────── */
.timer-row {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: #86efac;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.25);
  border-radius: 10px;
  padding: 9px 14px;
  transition: all 0.4s;
}

.timer-row svg {
  width: 17px;
  height: 17px;
  flex-shrink: 0;
}

.timer-warning {
  color: #fde68a;
  background: rgba(251, 191, 36, 0.1);
  border-color: rgba(251, 191, 36, 0.3);
}

.timer-expired {
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
}

/* ── Banners ────────────────────────────────────────────── */
.banner {
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
}

.error-banner {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.35);
  color: #fca5a5;
}

.info-banner {
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.3);
  color: #7dd3fc;
}

/* ── Buttons ────────────────────────────────────────────── */
.btn-primary {
  width: 100%;
  padding: 13px;
  background: linear-gradient(135deg, #6c47ff, #00c6ff);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 20px rgba(108, 71, 255, 0.4);
  letter-spacing: 0.3px;
}

.btn-primary:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
  box-shadow: 0 6px 28px rgba(108, 71, 255, 0.55);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  width: 100%;
  padding: 11px;
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.7);
  border: 1.5px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.btn-secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.btn-secondary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ── Spinner ────────────────────────────────────────────── */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
